
import Vue from "vue";
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Field from "@/components/crud/fields/FieldMixin.vue";
import FileHandler from "@tiptap/extension-file-handler";
import Image from "@tiptap/extension-image";

interface FormattingOption {
  command: () => void;
  isActive?: () => boolean;
  disabled?: () => boolean;
  icon?: string;
  label?: string;
}

export default Vue.extend({
  name: "RichTextEditor",
  mixins: [Field],
  components: { EditorContent },
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    requiredError: {
      type: Boolean,
      default: false,
    },
    minHeight: {
      type: Number,
      default: 120,
    },
    maxHeight: {
      type: Number,
      default: 240,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    isFocused: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editor: null as Editor | null,
      isFieldFocussed: false,
    };
  },
  computed: {
    computedPlaceholder(): string {
      return (
        this.placeholder ||
        this.label ||
        (this.field &&
          this.fieldGroup?.languagePrefix &&
          (this.$t(
            `${this.fieldGroup.languagePrefix}.${this.field}`
          ) as string)) ||
        ""
      );
    },
    editorStyles(): Record<string, string> {
      return {
        "--text-area-min-height": `${this.minHeight}px`,
        "--text-area-max-height": `${this.maxHeight}px`,
      };
    },
    formattingOptions(): FormattingOption[][] {
      return [
        ([1, 2, 3] as const).map((level) => ({
          command: () =>
            this.editor?.chain().focus().toggleHeading({ level }).run(),
          isActive: () => !!this.editor?.isActive("heading", { level }),
          label: `H${level}`,
        })),
        [
          {
            command: () => this.editor?.chain().focus().toggleBold().run(),
            isActive: () => !!this.editor?.isActive("bold"),
            icon: "formatBold",
          },
          {
            command: () => this.editor?.chain().focus().toggleItalic().run(),
            isActive: () => !!this.editor?.isActive("italic"),
            icon: "formatItalic",
          },
          {
            command: () => this.editor?.chain().focus().toggleUnderline().run(),
            isActive: () => !!this.editor?.isActive("underline"),
            icon: "formatUnderline",
          },
          {
            command: () =>
              this.editor?.chain().focus().toggleBulletList().run(),
            isActive: () => !!this.editor?.isActive("bulletList"),
            icon: "formatListBullet",
          },
          {
            command: () =>
              this.editor?.chain().focus().toggleOrderedList().run(),
            isActive: () => !!this.editor?.isActive("orderedList"),
            icon: "formatListNumbered",
          },
        ],
        [
          {
            command: () =>
              this.editor?.chain().focus().setTextAlign("left").run(),
            isActive: () => !!this.editor?.isActive({ textAlign: "left" }),
            icon: "formatAlignLeft",
          },
          {
            command: () =>
              this.editor?.chain().focus().setTextAlign("center").run(),
            isActive: () => !!this.editor?.isActive({ textAlign: "center" }),
            icon: "formatAlignCenter",
          },
          {
            command: () =>
              this.editor?.chain().focus().setTextAlign("right").run(),
            isActive: () => !!this.editor?.isActive({ textAlign: "right" }),
            icon: "formatAlignRight",
          },
        ],
      ];
    },
  },
  watch: {
    value(value) {
      const isSame = this.editor?.getHTML() === value;
      if (isSame) return;
      this.editor?.commands.setContent(value, false);
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        Image.configure({
          allowBase64: true,
        }),
        StarterKit,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Placeholder.configure({
          placeholder: this.computedPlaceholder,
        }),
        FileHandler.configure({
          allowedMimeTypes: [
            "image/png",
            "image/jpeg",
            "image/gif",
            "image/webp",
          ],
          onDrop: (currentEditor, files, pos) => {
            files.forEach((file) => {
              const fileReader = new FileReader();

              fileReader.readAsDataURL(file);
              fileReader.onload = () => {
                currentEditor
                  .chain()
                  .insertContentAt(pos, {
                    type: "image",
                    attrs: {
                      src: fileReader.result,
                    },
                  })
                  .focus()
                  .run();
              };
            });
          },
          onPaste: (currentEditor, files) => {
            files.forEach((file) => {
              const fileReader = new FileReader();

              fileReader.readAsDataURL(file);
              fileReader.onload = () => {
                currentEditor
                  .chain()
                  .insertContentAt(currentEditor.state.selection.anchor, {
                    type: "image",
                    attrs: {
                      src: fileReader.result,
                    },
                  })
                  .focus()
                  .run();
              };
            });
          },
        }),
      ],
      autofocus: this.autofocus,
      onBlur: () => {
        this.$emit("update:focused", false);
      },
      onUpdate: (e) => {
        this.$emit("input", e.editor.getHTML());
        this.$emit("hasContent", !!e.editor.getText());
      },
    });

    if (this.isFocused) {
      this.editor.commands.focus("end");
    }
  },
  methods: {
    clearContent() {
      this.editor?.commands.clearContent();
    },
  },
});
